import { type FunctionComponent } from 'react';
import { type ServerAnalyticsInterface } from '../../../../../@types/build';
import { type AnalyticsEvent } from '../../../helpers/analytics/analytics.helper';
import { Link } from '../../common-components/link/link.component';
import { CloudinaryImage, type CloudinaryImageProps } from '../../images/cloudinary-image/cloudinary-image.component';
import { ProductPrice, type ProductPriceProps } from '../product-price/product-price.component';
import { Rating } from '../rating/rating.component';

/**
 * ProductCard can either take all the expected props or it can pass just a
 * loading prop
 */
export type ProductCardProps = {
	brand: string;
	image: CloudinaryImageProps;
	model?: string;
	title: string;
	priceInfo?: ProductPriceProps;
	ratingValue?: number;
	url: string;
	serverAnalytics?: ServerAnalyticsInterface;
	analyticsEvent?: AnalyticsEvent | AnalyticsEvent[];
};

/**
 * renders a card to show truncated product information and acts as a link to
 * that product. The max width is 20em but there is an assumption that
 * the parent will set the width with a container
 */
export const ProductCard: FunctionComponent<ProductCardProps> = (props) => {
	const { url, image, brand, model = '', title, ratingValue = 0, priceInfo, serverAnalytics, analyticsEvent } = props;
	return (
		<Link
			url={url}
			color="grey-darker"
			className={`pa2 w-100 lh-title dib measure-narrow`}
			serverAnalytics={serverAnalytics}
			analyticsEvent={analyticsEvent}>
			<div className={'w-100'} data-testid="productCardImage">
				{<CloudinaryImage {...image} useDimensions={false} />}
			</div>
			<div className={'flex flex-column ph2 ph0-m f7 f6-ns'}>
				<span className="lh-copy tc3-copy pre-line mb1 min-h3-ns">
					<b className="mb1" data-testid="productCardBold">
						{brand} {model}
					</b>{' '}
					{title}
				</span>
				<div className="mb1 h1 f6" data-testid="productCardRating">
					{ratingValue > 0 && <Rating ratingValue={ratingValue} />}
				</div>
				{priceInfo && (
					<div className="mb1 f7" data-testid="productCardPrice">
						<ProductPrice {...priceInfo} />
					</div>
				)}
			</div>
		</Link>
	);
};
